import { Geo } from "@aws-amplify/geo";
import { validatePostalCode, formatPostalCode } from "services";

export interface AddressData {
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface LookupResult {
  success: boolean;
  data?: Partial<AddressData>;
  suggestions?: Array<{
    label: string;
    data: Partial<AddressData>;
  }>;
  error?: string;
  validationError?: string;
}

class PostalCodeLookup {
  private formatAddressFromPlace(place: any): Partial<AddressData> {
    let street = "";
    if (place.addressNumber && place.street) {
      street = `${place.addressNumber} ${place.street}`;
    } else if (place.street) {
      street = place.street1;
    }

    return {
      street1: street || undefined,
      street2: undefined,
      city: place.municipality || place.subRegion || undefined,
      state: place.region || undefined,
      postalCode: place.postalCode || undefined,
      country: place.country || undefined,
    };
  }

  async lookupByPostalCode(
    postalCode: string,
    country: string,
  ): Promise<LookupResult> {
    if (!postalCode || !country) {
      return { success: false, error: "Postal code and country are required" };
    }

    if (!validatePostalCode(postalCode, country)) {
      return {
        success: false,
        validationError: `Invalid postal code format for ${country}`,
      };
    }

    const formattedPostalCode = formatPostalCode(postalCode, country);

    try {
      const searchTerm = `${formattedPostalCode}, ${country}`;
      const result = await Geo.searchByText(searchTerm, {
        maxResults: 5,
      });

      if (!result || result.length === 0) {
        return {
          success: false,
          error: "No results found for this postal code",
        };
      }

      return this.processResults(result);
    } catch (error) {
      console.error("Postal code lookup failed:", error);
      return {
        success: false,
        error: "Address lookup service is currently unavailable",
      };
    }
  }

  private processResults(result: any[]): LookupResult {
    if (result.length === 1) {
      const data = this.formatAddressFromPlace(result[0]);

      return { success: true, data };
    } else {
      const suggestions = result.map((item) => ({
        label: item.label || `${item.municipality}, ${item.region}`,
        data: this.formatAddressFromPlace(item),
      }));
      return { success: true, suggestions };
    }
  }
}

export const createPostalLookupService = () => {
  return new PostalCodeLookup();
};
